import React, { useState } from "react";

/**
 * EtimsQRCode Component
 *
 * Displays a compact QR code that links to the eTims verification URL.
 * Uses the free QR Server API to generate QR codes - no npm package needed.
 * Designed to fit inside the payment details section of invoices.
 *
 * @param {Object} props
 * @param {string} props.ticketUrl - The eTims verification URL (from invoice.ticketUrl)
 * @param {number} props.size - Size of the QR code in pixels (default: 70)
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 */
const EtimsQRCode = ({ ticketUrl, size = 80, className = "", style = {} }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Don't render if no ticketUrl
  if (!ticketUrl) {
    return null;
  }

  // Generate QR code URL using free QR Server API
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(ticketUrl)}&margin=1`;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const containerStyle = {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    ...style,
  };

  const qrWrapperStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "#ffffff",
    padding: "6px",
    borderRadius: "4px",
    border: "1px solid #ddd",
  };

  const textStyle = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "4px",
    fontSize: "9px",
    lineHeight: "1",
  };

  const loadingStyle = {
    width: size,
    height: size,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f8f8",
    borderRadius: "3px",
  };

  if (imageError) {
    return (
      <div className={className} style={containerStyle}>
        <div style={qrWrapperStyle}>
          <div
            style={{
              width: size,
              height: size,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f9f9f9",
              borderRadius: "3px",
            }}
          >
            <i
              className="fas fa-qrcode"
              style={{ fontSize: "20px", color: "#ccc" }}
            ></i>
          </div>
          <div style={textStyle}>
            <span style={{ color: "#006633", fontWeight: "600" }}>KRA</span>
            <span style={{ color: "#888" }}>eTims</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={containerStyle}>
      <div style={qrWrapperStyle}>
        {/* Loading placeholder */}
        {!imageLoaded && (
          <div style={loadingStyle}>
            <div
              className="spinner-border spinner-border-sm text-secondary"
              role="status"
              style={{ width: "14px", height: "14px", borderWidth: "2px" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* QR Code Image */}
        <img
          src={qrCodeUrl}
          alt="eTims QR"
          width={size}
          height={size}
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{
            display: imageLoaded ? "block" : "none",
            borderRadius: "3px",
          }}
        />

        {/* Horizontal text below QR code */}
        <div style={textStyle}>
          <i
            className="fas fa-shield-alt"
            style={{ fontSize: "8px", color: "#006633" }}
          ></i>
          <span style={{ color: "#006633", fontWeight: "600" }}>KRA</span>
          <span style={{ color: "#888" }}>eTims</span>
        </div>
      </div>
    </div>
  );
};

/**
 * EtimsQRCodeCompact - Even smaller version for very tight spaces
 */
export const EtimsQRCodeCompact = ({ ticketUrl, size = 55 }) => {
  if (!ticketUrl) return null;

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(ticketUrl)}&margin=1`;

  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <img
        src={qrCodeUrl}
        alt="eTims QR"
        width={size}
        height={size}
        style={{
          borderRadius: "3px",
          border: "1px solid #ddd",
          padding: "3px",
          backgroundColor: "#fff",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "3px",
          marginTop: "3px",
          fontSize: "8px",
        }}
      >
        <span style={{ fontWeight: "600", color: "#006633" }}>KRA</span>
        <span style={{ color: "#888" }}>eTims</span>
      </div>
    </div>
  );
};

export default EtimsQRCode;
